import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { T, TranslationsContext, translation } from '../../../../../../../Translations';
import TemplateCard from './TemplateCard';
import { BlankIcon, CuratedIcon, QuickStartIcon } from './Icons';
import { LearningDesign } from '../../Types/SectionTemplates';

interface Props {
  onSelectBlank: () => void;
  onSelectBrowseAll: () => void;
  onSelectCurated: () => void;
  onClose: () => void;
  canAddBrowseAll: boolean;
  curatedSections: LearningDesign[];
  isLoadingCurated: boolean;
}

function SectionTypeSelector({
  onSelectBlank,
  onSelectBrowseAll,
  onSelectCurated,
  onClose,
  canAddBrowseAll,
  curatedSections,
  isLoadingCurated,
}: Props) {
  const ts = useContext(TranslationsContext);
  const hasCuratedSections = curatedSections.length > 0;
  const showCuratedOption = hasCuratedSections || isLoadingCurated;

  return (
    <div className="bg-card border border-border rounded-lg shadow-lg max-w-3xl w-full p-8">
      <div className="flex items-center justify-between mb-6">
        <h3
          style={{ fontWeight: 'var(--font-weight-semibold)' }}
          data-intercom-target="builder-v2-add-section-modal-title"
        >
          <T kind="Add a section" />
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 hover:bg-muted rounded transition-colors"
          aria-label={translation('Close dialog')(ts)}
          data-intercom-target="builder-v2-add-section-modal-close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <TemplateCard
          title="Blank"
          description="Start with an empty section and add specific courses"
          icon={<BlankIcon />}
          onClick={onSelectBlank}
          disabled={nothing()}
          badge={nothing()}
          intercomTarget="builder-v2-template-card-blank"
        />

        {showCuratedOption && (
          <TemplateCard
            title="Curated"
            description="Browse pre-built sections based on popular themes"
            icon={<CuratedIcon />}
            onClick={onSelectCurated}
            disabled={just(!hasCuratedSections || isLoadingCurated)}
            badge={isLoadingCurated ? just('Loading...') : nothing()}
            intercomTarget="builder-v2-template-card-curated"
          />
        )}

        <TemplateCard
          title="Quick Start"
          description="Add the full catalog of 120+ On-Demand Courses"
          icon={<QuickStartIcon />}
          onClick={onSelectBrowseAll}
          disabled={just(!canAddBrowseAll)}
          badge={!canAddBrowseAll ? just('Already added') : nothing()}
          intercomTarget="builder-v2-template-card-quick-start"
        />
      </div>
    </div>
  );
}

export default observer(SectionTypeSelector);
